import { zodResolver } from "@hookform/resolvers/zod"
import { ValidationMessage } from "@sop/SEEMP/SeempActionCard"
import { Button } from "@ui-lib/index"
import { requestApi2 } from "@utils/http"
import { endpoints } from "api"
import { Dispatch, SetStateAction } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { SeempSchemaConfigFE } from "./types"

export const NewActionDefinitionForm = z.object({
	type: z.string().trim().min(1, "Type is required"),
	unit: z.string().trim().min(1, "Unit is required"),
	description: z.string().trim().min(1, "Description is required"),
	yAxisLabel: z.string().trim().min(1, "Y-Axis Label is required"),
	optimization: z.enum(["minimize", "maximize"]),
})

export default function NewActionDefinitionComponent({
	schemaId,
	actionGroupId,
	setSeempSchemas,
}: {
	schemaId: number
	actionGroupId: number
	setSeempSchemas: Dispatch<SetStateAction<SeempSchemaConfigFE[]>>
}) {
	const {
		register,
		handleSubmit,
		formState: { isDirty, errors },
		control,
		reset,
		setValue,
		watch,
		trigger,
	} = useForm<z.infer<typeof NewActionDefinitionForm>>({
		resolver: zodResolver(NewActionDefinitionForm),
		defaultValues: {
			type: "",
			unit: "",
			description: "",
			yAxisLabel: "",
			optimization: "minimize",
		},
		mode: "onChange",
	})

	const description = watch("description")
	const type = watch("type")
	const unit = watch("unit")
	const yAxisLabel = watch("yAxisLabel")
	const optimization = watch("optimization")

	const handleCreateActionDefinition = async (schemaId: number, actionGroupId: number) => {
		await trigger()
		if (type === "" || unit === "" || description === "" || yAxisLabel === "") {
			alert("Please fill in all fields")
			return
		}
		const newActionDefinition = await requestApi2(endpoints.seempAddActionDefinition, {
			type,
			unit,
			description,
			yAxisLabel,
			optimization,
			isTrendLogicImplemented: false,
			actionGroupId: actionGroupId,
		})
		if (newActionDefinition !== null) {
			setSeempSchemas((prev) =>
				prev.map((schema) =>
					schema.id === schemaId
						? {
								...schema,
								actionGroups: schema.actionGroups.map((group) =>
									group.id === actionGroupId
										? {
												...group,
												actionsDefinitions: [
													...group.actionsDefinitions,
													newActionDefinition,
												],
											}
										: group
								),
							}
						: schema
				)
			)
			alert("Action definition added successfully")
			reset()
		} else {
			alert("Error adding action definition")
		}
	}
	return (
		<div className={`border-dipai-secondary-901 flex flex-col gap-3 border-1 px-3 py-2`}>
			<div className={`flex items-center justify-between border-b-1 pb-2`}>
				<p className={`text-dipai-secondary-901 text-menu font-semibold`}>
					Add new action definition
				</p>
				<Button
					variant={"filled-primary"}
					disabled={
						!(
							isDirty &&
							Object.keys(errors).length === 0 &&
							type !== "" &&
							description !== "" &&
							unit !== "" &&
							yAxisLabel !== ""
						)
					}
					onClick={() => handleCreateActionDefinition(schemaId, actionGroupId)}
				>
					<Button.Label>+ action definition</Button.Label>
				</Button>
			</div>
			<div className={`flex flex-col gap-1.5`}>
				<label
					className={`text-dipai-secondary-901 text-button h-4 leading-[0.875rem]`}
					htmlFor={`new_action_definition_type_actionGroup_${actionGroupId}`}
				>
					type
				</label>
				<input
					className={`h-8 min-w-50 rounded-sm border-1 px-2 outline-none focus:ring-1 ${errors.type !== undefined ? "border-red-500 hover:border-red-700 focus:ring-red-700" : "border-dipai-secondary-901/20 hover:border-dipai-secondary-500 focus:ring-dipai-secondary-500"}`}
					id={`new_action_definition_type_actionGroup_${actionGroupId}`}
					{...register("type")}
					placeholder={"enter a type for the action definition..."}
				/>
				{errors.type?.message !== undefined ? (
					<ValidationMessage
						type={"error"}
						validationMessage={errors.type.message}
					/>
				) : null}
			</div>
			<div className={`flex flex-col gap-1.5`}>
				<label
					className={`text-dipai-secondary-901 text-button h-4 leading-[0.875rem]`}
					htmlFor={`new_action_definition_description_actionGroup_${actionGroupId}`}
				>
					description
				</label>
				<input
					className={`h-8 min-w-50 rounded-sm border-1 px-2 outline-none focus:ring-1 ${errors.description !== undefined ? "border-red-500 hover:border-red-700 focus:ring-red-700" : "border-dipai-secondary-901/20 hover:border-dipai-secondary-500 focus:ring-dipai-secondary-500"}`}
					id={`new_action_definition_description_actionGroup_${actionGroupId}`}
					{...register("description")}
					placeholder={"enter a description for the action definition..."}
				/>
				{errors.description?.message !== undefined ? (
					<ValidationMessage
						type={"error"}
						validationMessage={errors.description.message}
					/>
				) : null}
			</div>
			<div className={`grid grid-cols-[1fr_2fr_auto] gap-3`}>
				<div className={`flex flex-col gap-1.5`}>
					<label
						className={`text-dipai-secondary-901 text-button h-4 leading-[0.875rem]`}
						htmlFor={`new_action_definition_unit_actionGroup_${actionGroupId}`}
					>
						unit
					</label>
					<input
						className={`h-8 min-w-50 rounded-sm border-1 px-2 outline-none focus:ring-1 ${errors.unit !== undefined ? "border-red-500 hover:border-red-700 focus:ring-red-700" : "border-dipai-secondary-901/20 hover:border-dipai-secondary-500 focus:ring-dipai-secondary-500"}`}
						id={`new_action_definition_unit_actionGroup_${actionGroupId}`}
						{...register("unit")}
						placeholder={"enter a unit for the action definition..."}
					/>
					{errors.unit?.message !== undefined ? (
						<ValidationMessage
							type={"error"}
							validationMessage={errors.unit.message}
						/>
					) : null}
				</div>
				<div className={`flex flex-col gap-1.5`}>
					<label
						className={`text-dipai-secondary-901 text-button h-4 leading-[0.875rem]`}
						htmlFor={`new_action_definition_yAxisLabel_actionGroup_${actionGroupId}`}
					>
						y-axis label
					</label>
					<input
						className={`h-8 min-w-50 rounded-sm border-1 px-2 outline-none focus:ring-1 ${errors.yAxisLabel !== undefined ? "border-red-500 hover:border-red-700 focus:ring-red-700" : "border-dipai-secondary-901/20 hover:border-dipai-secondary-500 focus:ring-dipai-secondary-500"}`}
						id={`new_action_definition_yAxisLabel_actionGroup_${actionGroupId}`}
						{...register("yAxisLabel")}
						placeholder={"enter a yAxisLabel for the action definition..."}
					/>
					{errors.yAxisLabel?.message !== undefined ? (
						<ValidationMessage
							type={"error"}
							validationMessage={errors.yAxisLabel.message}
						/>
					) : null}
				</div>
				<div className={`flex flex-col gap-1.5`}>
					<label
						className={`text-dipai-secondary-901 text-button h-4 leading-[0.875rem]`}
						htmlFor={`new_action_definition_optimization_actionGroup_${actionGroupId}`}
					>
						optimization
					</label>
					<select
						className={`h-8 w-fit py-0 outline-none focus:ring-1 ${errors.optimization !== undefined ? "border-red-500 hover:border-red-700 focus:ring-red-700" : "border-dipai-secondary-901/20 hover:border-dipai-secondary-500 focus:ring-dipai-secondary-500"}`}
						id={`new_action_definition_optimization_actionGroup_${actionGroupId}`}
						{...register("optimization")}
					>
						<option value={"minimize"}>minimize</option>
						<option value={"maximize"}>maximize</option>
					</select>
					{errors.optimization?.message !== undefined ? (
						<ValidationMessage
							type={"error"}
							validationMessage={errors.optimization.message}
						/>
					) : null}
				</div>
			</div>
		</div>
	)
}
